import React, { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Page, Spinner, Text } from '@shopify/polaris'
import _ from 'lodash'
import { Chart as ChartJS, registerables } from 'chart.js'
import PageHelmet from 'components/PageHelmet'
import BalanceComponent from '../DashboardElements/BalanceComponent/BalanceComponent'
import SubscriptionComponent from '../DashboardElements/SubscriptionComponent/SubscriptionComponent'
import CreditsComponent from '../DashboardElements/CreditsComponent/CreditsComponent'
import DashboardHeader from './components/DashboardHeader'
import DashboardPrimaryAction from './components/DashboardPrimaryAction'
import SubscriptionDialog from './components/SubscriptionDialog'
import DueDatesSection from './components/DueDatesSection'
import VatReturnsSection from './components/VatReturnsSection'
import SalesTaxReturnsSection from './components/SalesTaxReturnsSection'
import EPRReturnsSection from './components/EPRReturnsSection'
import NewsSection from './components/NewsSection'
import ThresholdUSSection from './components/ThresholdUSSection'
import ThresholdEUSection from './components/ThresholdEUSection'
import DeemedVatSection from './components/DeemedVatSection'
import TransactionsByCountryOmpSection from './components/TransactionsByCountryOmpSection'
import RemittedSalesTaxSection from './components/RemittedSalesTaxSection'
import BonusProgramSection from './components/BonusProgramSection'
import TaxAgentClientsSection from './components/TaxAgentClientsSection'
import { useDashboardData } from './hooks/useDashboardData'
import { useActionDialogs } from './hooks/useActionDialogs'
import { isTaxAgentSelector } from '../../../redux/selectors'
import '../style.scss'

ChartJS.register(...registerables)

const Dashboard = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const user = useSelector((state) => state.user.user)
	const currentPlan = useSelector((state) => state.subscription.currentPlan)
	const lastUploaded = useSelector((state) => state.dashboard.lastUploaded)
	const fetchingPaymentInfo = useSelector((state) => state.profile.fetchingPaymentInfo)
	const thresholdUS = useSelector((state) => state.thresholds.thresholdUS)
	const thresholdEU = useSelector((state) => state.thresholds.thresholdEU)
	const bonusProgram = useSelector((state) => state.tax.bonusProgram)
	const fetchingBonus = useSelector((state) => state.tax.fetchingBonus)
	const openProjectName = useSelector((state) => state.user.openProjectName)
	const dueDates = useSelector((state) => state.dueDates.dueDates)
	const fetchingDueDates = useSelector((state) => state.dueDates.fetching)
	const declarations = useSelector((state) => state.declarations.declarations)
	const fetchingDeclarations = useSelector((state) => state.declarations.fetching)
	const declarationsSalesTax = useSelector((state) => state.declarations.declarationsSalesTax)
	const fetchingSalesTax = useSelector((state) => state.declarations.fetchingSalesTax)
	const declarationsEpr = useSelector((state) => state.declarations.declarationsEpr)
	const fetchingEPR = useSelector((state) => state.declarations.fetchingListEPR)
	const news = useSelector((state) => state.dashboard.news)
	const fetchingNews = useSelector((state) => state.dashboard.fetchingNews)
	const paymentInfo = useSelector((state) => state.profile.paymentInfo)
	const deemedVat = useSelector((state) => state.dashboard.deemedVat)
	const fetchingDeemedVat = useSelector((state) => state.dashboard.fetchingDeemedVat)
	const transByCountry = useSelector((state) => state.dashboard.transByCountry)
	const fetchingTransByCountry = useSelector((state) => state.dashboard.fetchingTransByCountry)
	const fetchingThresholdUS = useSelector((state) => state.thresholds.fetchingThresholdUS)
	const fetchingThresholdEU = useSelector((state) => state.thresholds.fetchingThresholdEU)

	const { actionDialogs, handleActionDialogsOpen, handleActionDialogsClose } = useActionDialogs()

	useDashboardData(user)

	const IS_TAX_AGENT = useMemo(() => isTaxAgentSelector(user), [user])

	const handleSubscriptionDialogClose = useCallback(() => {
		handleActionDialogsClose("subscription")
	}, [handleActionDialogsClose])

	if (!user || _.isEmpty(user)) {
		return (
			<div style={{ padding: "1rem" }}>
				<Spinner />
			</div>
		)
	}

	return (
		<Page
			fullWidth
			title={<DashboardHeader user={user} t={t} />}
			subtitle={
				user.company &&
				user.company.id !== user.tax_agent_company_id &&
				!(user.is_manager && user.company.is_tax_agent) && (
					<Text variant='bodyLg' as='span' color='subdued'>
						{user.is_manager.name}
						{t("home.how")}
					</Text>
				)
			}
			primaryAction={
				<DashboardPrimaryAction
					user={user}
					currentPlan={currentPlan}
					lastUploaded={lastUploaded}
					t={t}
					onSubscriptionDialogOpen={handleActionDialogsOpen}
					navigate={navigate}
				/>
			}
		>
			<PageHelmet title='Home' />

			<div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
				<div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 60%', minWidth: '500px' }}>
					<div style={{
						display: "flex",
						flexDirection: "column",
						width: '100%',
					}}>
						{user.is_omp && openProjectName !== 'LEPR' && (
							<div className='sales-tax-returns' style={{ margin: 0 }}>
								<DeemedVatSection
									deemedVat={deemedVat}
									fetchingDeemedVat={fetchingDeemedVat}
									t={t}
								/>
							</div>
						)}

						{!user.company.is_tax_agent && (
							<div className='sales-tax-returns' style={{ marginTop: 0 }}>
								<DueDatesSection
									dueDates={dueDates}
									fetchingDueDates={fetchingDueDates}
									user={user}
									t={t}
									navigate={navigate}
								/>
							</div>
						)}

						{!(user.is_manager && user.company.is_tax_agent) &&
							user.company.id !== user.tax_agent_company_id &&
							!user.is_omp && currentPlan?.subscriptions && openProjectName === 'LTax' && (
								<div className='sales-tax-returns'>
									<VatReturnsSection
										declarations={declarations}
										fetchingDeclarations={fetchingDeclarations}
										user={user}
										t={t}
										navigate={navigate}
									/>
								</div>
							)}

						{!(user.is_manager && user.company.is_tax_agent) &&
							user.company.id !== user.tax_agent_company_id &&
							!user.is_omp && currentPlan?.subscriptions_sales_tax && openProjectName === 'Ltax' && (
								<div className='sales-tax-returns'>
									<SalesTaxReturnsSection
										declarationsSalesTax={declarationsSalesTax}
										fetchingSalesTax={fetchingSalesTax}
										user={user}
										t={t}
										navigate={navigate}
									/>
								</div>
							)}

						{!(user.is_manager && user.company.is_tax_agent) &&
							user.company.id !== user.tax_agent_company_id &&
							currentPlan?.subscriptions_epr && openProjectName === 'LEPR' && (
								<div className='epr-returns'>
									<EPRReturnsSection
										declarationsEpr={declarationsEpr}
										fetchingEPR={fetchingEPR}
										user={user}
										t={t}
										navigate={navigate}
									/>
								</div>
							)}

						{user.is_omp && openProjectName === 'LTax' && (
							<div className='sales-tax-returns' style={{ marginTop: 0 }}>
								<RemittedSalesTaxSection t={t} />
							</div>
						)}

						<NewsSection
							news={news}
							fetchingNews={fetchingNews}
							t={t}
							navigate={navigate}
						/>
					</div>
				</div>

				<div style={{ flex: '1 1 25%', minWidth: '300px' }}>
					<BalanceComponent />

					{IS_TAX_AGENT && (
						<BonusProgramSection
							bonusProgram={bonusProgram}
							fetchingBonus={fetchingBonus}
							t={t}
						/>
					)}

					<SubscriptionComponent />
					<CreditsComponent />

					{!user.is_tax_agent && <br />}
					{!IS_TAX_AGENT && openProjectName !== 'LEPR' && (
						<>
							{thresholdUS && thresholdUS.items && !_.isEmpty(thresholdUS.items) && (
								<ThresholdUSSection
									thresholdUS={thresholdUS}
									fetchingThresholdUS={fetchingThresholdUS}
									t={t}
									navigate={navigate}
								/>
							)}
							{thresholdEU && (
								<ThresholdEUSection
									thresholdEU={thresholdEU}
									fetchingThresholdEU={fetchingThresholdEU}
									t={t}
									navigate={navigate}
								/>
							)}
							{thresholdEU && <br />}
						</>
					)}

					{IS_TAX_AGENT && (
						<TaxAgentClientsSection
							paymentInfo={paymentInfo}
							fetchingPaymentInfo={fetchingPaymentInfo}
							t={t}
						/>
					)}

					{user.is_omp && openProjectName !== 'LEPR' && (
						<TransactionsByCountryOmpSection
							transByCountry={transByCountry}
							fetchingTransByCountry={fetchingTransByCountry}
							t={t}
						/>
					)}
				</div>
			</div>

			<SubscriptionDialog
				t={t}
				isOpen={actionDialogs.subscription.open}
				onClose={handleSubscriptionDialogClose}
			/>
			<br />
		</Page>
	)
}

export default Dashboard

